import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import { Connection } from '@3d-scene/model3d/connection';
import { BufferGeometry, CylinderGeometry, Group, Material, Mesh, Object3D } from 'three';
import { ProjectModel } from '../model/project.model';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { LabeledFlag } from '@3d-scene/model3d/labeled-flag';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';
import { ProjectArticleService } from '../service/project-article.service';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { WireBridgeService } from '../components/article/manager/wire-bridge.service';

@Injectable({providedIn: 'root'})
export class WireBridgeResizeService {

  private _wireResolutionMin = 5;
  private _wireResolutionMax = 15;

  private previousGUID: string = '';
  private multiWireCounter: number = -1;
  private singleWireCounter: number = -1;

  constructor(
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private containerService: ContainerService,
    private wireContourService: WireContourService,
  ) {
    this.init();
  }

  public init(): void {
    this.projectModel.onWireBridgeCountersReset$.subscribe({
      next: () => {
        this.multiWireCounter = -1;
        this.singleWireCounter = -1;
      },
      error: (error) => Log.error('Error in onWireBridgeCountersReset subscription: ', error)
    })
  }

  /** performance optimization to lower the resolution for projects with many wirebridges */
  public updateWireResolution(wiresInProjectAmount: number): void {
    this._wireResolutionMin = Math.ceil(MathUtils.mapRange(wiresInProjectAmount, 5, 60, 8, 4));
    this._wireResolutionMax = Math.ceil(MathUtils.mapRange(wiresInProjectAmount, 5, 90, 24, 8));
  }

  public createWireBridges(wireBridgeContainers: ArticleContainer[], skipWireLengthCalculation: boolean, updateCableVisuals: boolean): void {
    if (!skipWireLengthCalculation) {
      this.setWireJumperLengthProperty(wireBridgeContainers, true);
    }
    this.adjustWireBridges(wireBridgeContainers, updateCableVisuals);
  }

  public async updateWireBridges(movedWireBridgeContainers: ArticleContainer[], updateCableVisuals: boolean): Promise<void> {
    // since this can be slow with large amounts of wires, we split the array in chunks and wait for 1ms between each chunk so the ui doesn't freeze
    const chunks: ArticleContainer[][] = [];
    const chunkSize = 20;
    for (let i = 0; i < movedWireBridgeContainers.length; i += chunkSize) {
      chunks.push(movedWireBridgeContainers.slice(i, i + chunkSize));
    }

    for (const chunk of chunks) {
      await new Promise(resolve => setTimeout(resolve, 1));
      for (const movedWireBridgeContainer of chunk) {
          const connectedUsageIds = this.projectArticleService.collectGroupArticleUsageIds(movedWireBridgeContainer.articleUsage);
          const connectedContainers = connectedUsageIds.map(usageId => this.containerService.findArticleContainerInGlobal(usageId));

          this.setWireJumperLengthProperty(connectedContainers, false);
          this.adjustWireBridges(movedWireBridgeContainers, updateCableVisuals);
      }
    }
  }

  /** @returns the connection on the RailArticle the wireBridge sits on */
  private getWireBridgeContainersGivingConnection(wireBridgeContainer: ArticleContainer): Connection {
    const meteElementUsageId = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(wireBridgeContainer.articleUsage);
    const articleContainer = this.containerService.findArticleContainerInGlobal(meteElementUsageId.articleUsageId);
    return articleContainer.findConnectionByName(meteElementUsageId.metaElementName);
  }

  private connectArticleContainersWithWire(containers: ArticleContainer[], material: Material, radius: number): void {
    // loop through container pairs that need a connecting wire between them (0-1, 1-2, 2-3....)
    const geometries = this.connectContainersWithWire(containers, radius);

    const wireGroup = this.createConnectingWireGroup(geometries, material);

    containers[0].addChildToModelAndKeepWorldTransform(wireGroup);
    this.wireContourService.createWireContour(containers[0]);
    Object3DUtils.hideInGeometryRepresentation(containers[0].wirecontour);
  }

  private connectContainersWithWire(containers: ArticleContainer[], radius: number): BufferGeometry[] {
    const geometries: BufferGeometry[] = [];
    for (let i = 1; i < containers.length; i++) {
      if (i == 1) {
        this.removeWireJumperGroup(containers[i - 1]);
      }
      this.removeWireJumperGroup(containers[i]);
      const geometry = this.connectTwoContainersWithWire(containers[i - 1], containers[i], radius);
      geometries.push(geometry);
    }
    return geometries;
  }

  private connectTwoContainersWithWire(container1: ArticleContainer, container2: ArticleContainer, radius: number): BufferGeometry {
    const start = new Object3D();
    const end = new Object3D();
    start.position.copy(container1.position);
    end.position.copy(container2.position);
    const length = start.position.distanceTo(end.position);

    this.rotateToGivingConnection(start, container1);
    this.rotateToGivingConnection(end, container2);

    this.removeWireJumperGroup(container1);
    this.removeWireJumperGroup(container2);

    // the longer the wire, the less curved it has to be and the more segments are needed for a smooth curve
    const curvature = MathUtils.mapRange(length, 100, 1500, 0.8, 0.5);

    const segments = Math.ceil(MathUtils.mapRange(length, 100, 1500,
      this._wireResolutionMin,
      this._wireResolutionMax));

    return MeshUtils.createCurvedTube(start, end, curvature, radius, segments, 5);
  }

  private createOpenEndedWire(container: ArticleContainer, material: Material, radius: number) {
    container.model.children.forEach(c => c.name == WireBridgeService.WIRE_OBJECT_NAME ? container.removeChildFromModel(c) : null);

    const height = radius * 2 + 40;

    this.removeWireJumperGroup(container);

    const cylinder = new CylinderGeometry(radius, radius, height, 8);

    const mesh: Mesh = new Mesh(cylinder, material);
    mesh.position.copy(container.position);
    this.rotateToGivingConnection(mesh, container);
    mesh.translateY(height / 2);
    MeshUtils.applyTransform(mesh);

    const wireGroup = new Group();
    wireGroup.name = WireBridgeService.WIRE_OBJECT_NAME;
    wireGroup.add(mesh);
    Object3DUtils.hideInGeometryRepresentation(wireGroup);

    container.addChildToModelAndKeepWorldTransform(wireGroup);
    this.wireContourService.createWireContour(container);
    Object3DUtils.hideInGeometryRepresentation(container.wirecontour);
  }

  private createConnectingWireGroup(geometries: BufferGeometry[], material: Material): Group {
    const geometry = geometries.length == 1 ?
      geometries[0]
      : BufferGeometryUtils.mergeGeometries(geometries);

    const mesh: Mesh = new Mesh(geometry, material);
    const wireGroup: Group = new Group();
    wireGroup.name = WireBridgeService.WIRE_OBJECT_NAME;

    wireGroup.add(mesh);
    Object3DUtils.hideInGeometryRepresentation(wireGroup);
    return wireGroup;
  }

  public removeWireJumperGroup(articleContainer: ArticleContainer): void {
    if (!ArticleContainer) {
      return;
    }
    for (const childContainer of articleContainer.model.children) {
      if (childContainer.name === WireBridgeService.WIRE_OBJECT_NAME) {
        articleContainer.removeChildFromModel(childContainer);
        break;
      }
    }
  }


  private setWireJumperLengthProperty(containers: ArticleContainer[], includeOpenEndedWireBridges: boolean): void {
    if ((!includeOpenEndedWireBridges && containers.length == 1) || containers.length == 0) {
      return;
    }

    let crossSection = PropertyUtils.getFloatPropertyValue(containers[0].article.connectionFeatures, ArticleConnectionPropertyDef.CROSS_SECTION);

    if (isNaN(crossSection)) {
      crossSection = 0;
      Log.error(`Could not find crossSection for wireBridge ${containers[0].article.articleNo}`);
    }

    const length = this.calculateWireBridgeLengthProperty(containers, crossSection);

    /* External clients (pdf generation, calculating the overall required cable length etc.)
     expect only one of the articleContainers to have a cableLength, so we set it only in the first occurring container. */
    ArticleUsageUtils.setArticleUsageInfoPropertyIntValue(
      containers[0].articleUsage, ArticleUsageInfoPropertyDef.CABLE_LENGTH, length);

    for (let i = 1; i < containers.length; i++) {
      ArticleUsageUtils.removeArticleUsageInfoProperty(containers[i].articleUsage, ArticleUsageInfoPropertyDef.CABLE_LENGTH);
    }
  }

  private calculateWireBridgeLengthProperty(containers: ArticleContainer[], crossSection: number): number {
    let accumulatedDistance = 0;

    if (containers.length > 1) {
      for (let i = 1; i < containers.length; i++) {
        const distance = containers[i - 1].position.distanceTo(containers[i].position) / 10;
        accumulatedDistance += distance;
      }
    }

    // since the cables need space to bend, we add more length depending on their crossSection (thin cables bend easier)
    const leeway = crossSection < 2.5 ? 45 : (Math.sqrt(crossSection * 4 / Math.PI)) * 25;

    const length = accumulatedDistance + leeway;

    // round the length to the next tenth.
    return Math.ceil(length / 10) * 10;
  }

  private getRenderedWireCrossSection(wireContainer: ArticleContainer): number {
    const crossSectionProperty = PropertyUtils.getFloatPropertyValue(wireContainer.article.connectionFeatures, ArticleConnectionPropertyDef.CROSS_SECTION);
    return crossSectionProperty > 0.01 ? crossSectionProperty : 0.5;
  }

  private adjustWireBridges(wireBridgeContainers: ArticleContainer[], updateCableVisuals: boolean): void {
    const processedWireBridgeIds: string[] = [];
    for (const container of wireBridgeContainers) {
      if (processedWireBridgeIds.indexOf(container.articleGUID) >= 0) {
        continue;
      }
      const groupedUsageIds: string[] = this.projectArticleService.collectGroupArticleUsageIds(container.articleUsage);
      const groupedContainers: ArticleContainer[] = groupedUsageIds.map(usageId => this.containerService.findArticleContainerInGlobal(usageId));
      this.adjustGroupedWireBridges(groupedContainers, updateCableVisuals);
      ArrayUtils.mergeArrays(processedWireBridgeIds, ArticleContainerUtils.extractArticleGUIDs(groupedContainers));
    }
  }

  private adjustGroupedWireBridges(groupedWireBridgeContainers: ArticleContainer[], updateCableVisuals: boolean): void {
    const excludedMeshNames: string[] = [];
    for (const connection of groupedWireBridgeContainers[0].connections) {
      excludedMeshNames.push(connection.id);
    }
    const material: Material = Object3DUtils.findFirstMaterial(groupedWireBridgeContainers[0].model, excludedMeshNames);
    const radius = this.getRenderedWireCrossSection(groupedWireBridgeContainers[0]) * 10 / 2;

    if (groupedWireBridgeContainers.length > 1 && updateCableVisuals) {
      this.connectArticleContainersWithWire(groupedWireBridgeContainers, material, radius);
    } else if (updateCableVisuals) {
      this.createOpenEndedWire(groupedWireBridgeContainers[0], material, radius)
    }

    for (const container of groupedWireBridgeContainers) {
      this.updateWireContourAndFlags(container, material, radius);
    }
  }

  private updateWireContourAndFlags(container: ArticleContainer, material: Material, radius: number): void {
    if (container.model) {
      this.wireContourService.validateWireContour(container);
    }

    if (!container.getObjectByName(WireBridgeService.FLAG_OBJECT_NAME)) {
      const flag: Group = this.createWireBridgeFlag(container, material, radius);
      container.addChildToModelAndKeepWorldTransform(flag);
    }
    Object3DUtils.hideInGeometryRepresentation(container.wirecontour);
  }


  private createWireBridgeFlag(container: ArticleContainer, material: Material, radius: number): Group {
    const articleUsage: ArticleUsageVO = container.articleUsage;
    let label: string = '';
    const previousLabel: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.WIRE_LABEL);
    if (!previousLabel) {
      if (articleUsage.groupId && this.projectArticleService.findArticleUsageIdsByGroupId(articleUsage.groupId).length > 1) {
        const isNewWireBridge: boolean = this.previousGUID !== articleUsage.groupId;
        label = this.getMultiWireText(isNewWireBridge);
        this.previousGUID = articleUsage.groupId;
      } else {
        label = this.getSingleWireText();
      }
    } else {
      label = previousLabel;
      this.updateCounters(label);
    }
    ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(articleUsage, ArticleUsageInfoPropertyDef.WIRE_LABEL, label);
    const flag: LabeledFlag = new LabeledFlag(
      container,
      label,
      material,
      radius,
      this.projectModel.texturesEnabled,
    );
    flag.position.copy(container.position);
    this.rotateToGivingConnection(flag, container);
    flag.name = WireBridgeService.FLAG_OBJECT_NAME;
    return flag;
  }

  private rotateToGivingConnection(object: Object3D, wireBridgeContainer: ArticleContainer): void {
    const rotation = this.getWireBridgeContainersGivingConnection(wireBridgeContainer).getRotationEuler();
    object.setRotationFromEuler(rotation);
    object.rotateX(Math.PI / 2);
  }

  private getSingleWireText(): string {
    this.singleWireCounter++;

    const majorLetter: number = this.singleWireCounter % 26;
    const minorNumber: number = Math.floor(this.singleWireCounter / 26);

    let fullText: string = String.fromCharCode('A'.charCodeAt(0) + majorLetter);
    fullText += fullText;
    if (minorNumber > 0) {
      fullText += '' + (minorNumber + 1);
    }
    return fullText;
  }

  private getMultiWireText(newWireBridge: boolean = false): string {
    if (newWireBridge) {
      this.multiWireCounter++;
    }

    const majorLetter: number = this.multiWireCounter % 26;
    const minorNumber: number = Math.floor(this.multiWireCounter / 26);

    let fullText: string = String.fromCharCode('A'.charCodeAt(0) + majorLetter);
    if (minorNumber > 0) {
      fullText += '' + (minorNumber + 1);
    }
    return fullText;
  }

  private updateCounters(label: string): void {
    const minorValue: number = label.charCodeAt(0) - 'A'.charCodeAt(0);
    const majorValue: number = parseInt(label.substring(label.length - 1), 10);
    let storedCounter: number = minorValue;
    if (NumberUtils.isNumber(majorValue)) {
      storedCounter += (majorValue - 1) * 26;
    }

    if (label.length === 1 || NumberUtils.isNumber(parseInt(label.charAt(1), 10))) {
      // we have multi wire bridge
      if (NumberUtils.isNumber(storedCounter) && storedCounter >= this.multiWireCounter) {
        this.multiWireCounter = storedCounter;
      }
    } else {
      // we have single wire bridge
      if (NumberUtils.isNumber(storedCounter) && storedCounter >= this.singleWireCounter) {
        this.singleWireCounter = storedCounter;
      }
    }
  }
}
